/**
 * Decodificação do From/To das chamadas, com a mesma convenção do softphone legado:
 * o dialplan coloca no display name `nome%<sysid codificado>%<callid>` (ordem livre; o segmento
 * que contém "." é o callid do Asterisk, o outro é o sysid em pares de dígitos decimais = char codes).
 */

export type ParsedCaller = {
  /** Nome amigável sem os metadados (`%...`). */
  displayName: string;
  /** Usuário da URI SIP (número/ramal). */
  user: string;
  /** "Nome <sip:user@host>" — exatamente o que o legado mandava como phoneInfo. */
  phoneInfo: string;
  /** sysid já decodificado (minúsculo), ex.: "pl…" (plataforma) ou sys_id do ServiceNow. */
  sysid: string | null;
  /** sysid como veio (pares de dígitos) — usado no `%sysid%` das macros. */
  sysidRaw: string | null;
  /** uniqueid do Asterisk (ex.: "1712345678.123"). */
  callid: string | null;
};

type UriLike = { scheme?: string; user?: string; host?: string };

/** "112108..." → "pl…" (pares de dígitos decimais = códigos de caractere). */
export function decodeSysid(encoded: string): string {
  let out = "";
  for (let i = 0; i < encoded.length; i += 2) {
    const code = Number(encoded.substring(i, i + 2));
    if (!Number.isFinite(code)) continue;
    out += String.fromCharCode(code);
  }
  return out.toLowerCase();
}

export function parseCaller(displayNameRaw: string | null | undefined, uri: UriLike | null | undefined): ParsedCaller {
  const user = uri?.user ?? "";
  let displayName = "";
  let sysidRaw: string | null = null;
  let callid: string | null = null;

  const raw = (displayNameRaw ?? "").trim();
  if (raw) {
    const parts = raw.split("%");
    if (parts.length < 2) {
      displayName = raw;
    } else {
      displayName = parts[0];
      for (const seg of parts.slice(1, 3)) {
        if (!seg) continue;
        if (seg.includes(".")) callid = seg;
        else sysidRaw = seg;
      }
    }
  }
  if (!displayName) displayName = user;

  const scheme = uri?.scheme ?? "sip";
  const host = uri?.host ?? "";
  const phoneInfo = `${displayName} <${scheme}:${user}${host ? `@${host}` : ""}>`;

  return {
    displayName,
    user,
    phoneInfo,
    sysid: sysidRaw ? decodeSysid(sysidRaw) : null,
    sysidRaw,
    callid,
  };
}

/** Nome curto para exibição: "Maria (11999990000)" ou só o número. */
export function callerLabel(c: Pick<ParsedCaller, "displayName" | "user">): string {
  if (!c.displayName || c.displayName === c.user) return c.user || "Desconhecido";
  return c.user ? `${c.displayName} (${c.user})` : c.displayName;
}
